// ============================================================================
// CrewAhead Maritime Platform - Seafarer API Functions
// ============================================================================
// Seafarer profiles, sea service records, certifications, documents,
// search (employer-only), and dashboard.

import type { SeafarerProfile } from '@/lib/types/models';
import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SeafarerProfileInput {
  currentRankId?: number | null;
  targetRankId?: number | null;
  departmentId?: number | null;
  primaryVesselCategoryId?: number | null;
  yearsOfExperience?: number | null;
  availabilityStatusId?: number | null;
  availabilityStatus?: string | null;
  availableFrom?: string | null;
  preferredContractDurationId?: number | null;
  preferredContractDuration?: number | null;
  expectedSalaryUsd?: number | null;
  preferredVesselTypeIds?: number[];
  preferredDepartmentIds?: number[];
  preferredTradingAreaIds?: number[];
  preferredTradingAreas?: string[];
  preferredContractTypeIds?: number[];
  willingToRelocate?: boolean;
  /** Language taxonomy IDs (integer PKs from seamaster.Language). */
  languages?: number[];
  /** Skill taxonomy IDs (integer PKs). */
  skills?: number[];
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  hasTankerExperience?: boolean;
  monthsInRank?: number | null;
  monthsWithOperator?: number | null;
  monthsOnVesselCategory?: number | null;
  isCargoWatchkeeper?: boolean;
}

export interface SeaServiceRecord {
  id: string;
  vesselName: string;
  vesselType: { id: string; vesselTypeName: string } | null;
  imoNumber: string | null;
  rank: { id: string; rankName: string } | null;
  rankOther?: string | null;
  employerName: string | null;
  agencyName: string | null;
  dateFrom: string;
  dateTo: string | null;
  isCurrent: boolean;
  grosstonnage: number | null;
  enginePowerKw: number | null;
  tradingArea: string | null;
  duties: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeaServiceRecordInput {
  vesselName: string;
  vesselType?: number | null;
  imoNumber?: string;
  rank?: number | null;
  employerName?: string;
  agencyName?: string;
  dateFrom: string;
  dateTo?: string | null;
  isCurrent?: boolean;
  grosstonnage?: number;
  enginePowerKw?: number;
  tradingArea?: string;
  duties?: string;
}

export interface SeafarerCertification {
  id: string;
  certName: string;
  certificateNumber: string | null;
  issuingAuthority: string | null;
  issueDate: string;
  expiryDate: string | null;
  certificationType: { id: string; certName: string } | null;
  isVerified: boolean;
  documentUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeafarerCertificationInput {
  certificationTypeId: string | null;
  certName?: string;
  certNameCustom?: string;
  certificateNumber?: string;
  issuingAuthority?: string;
  issueDate: string;
  expiryDate?: string | null;
  documentUrl?: string;
  notes?: string | null;
}

export interface SeafarerDocument {
  id: string;
  documentType: string;
  documentNumber: string | null;
  issuingAuthority: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  isPrimary: boolean;
  isVerified: boolean;
  documentFile: string | null;
  fileSize: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeafarerDocumentInput {
  documentType: string;
  documentNumber?: string;
  issuingCountry?: string;
  issuingAuthority?: string;
  issueDate?: string;
  expiryDate?: string | null;
  isPrimary?: boolean;
  documentFile?: File;
}

export interface SeafarerVisaInput {
  documentType?: 'visa';
  documentNumber?: string;
  issuingCountry?: string;
  expiryDate?: string | null;
  isPrimary?: boolean;
}

export interface SeafarerDashboardData {
  detail: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  stats: {
    profileCompletionPercent: number;
    seaServiceCount: number;
    certificationCount: number;
    documentCount: number;
    expiringCertifications: number;
    expiredCertifications: number;
    expiringDocuments: number;
  };
  profile: {
    currentRankName: string | null;
    targetRankName: string | null;
    yearsOfExperience: number | null;
    availabilityStatus: string | null;
  } | null;
}

// ─── Profile ─────────────────────────────────────────────────────────────────

/**
 * GET /seafarers/profiles/my-profile/
 *
 * Get the current user's seafarer profile.
 * Creates the profile if it doesn't exist.
 *
 * @returns Seafarer profile
 */
export async function getMySeafarerProfile(): Promise<SeafarerProfile> {
  try {
    const response = await apiClient.get<SeafarerProfile>(
      API_ENDPOINTS.SEAFARER.MY_PROFILE
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /seafarers/profiles/my-profile/
 *
 * Update the current user's seafarer profile.
 *
 * @param data - Partial profile update data
 * @returns Updated seafarer profile
 */
export async function updateMySeafarerProfile(
  data: Partial<SeafarerProfileInput>
): Promise<SeafarerProfile> {
  try {
    const response = await apiClient.patch<SeafarerProfile>(
      API_ENDPOINTS.SEAFARER.MY_PROFILE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /seafarers/profiles/
 *
 * List seafarer profiles. Employers/admins see all;
 * regular users see only their own.
 *
 * @param params - Optional search and pagination
 * @returns Paginated list of seafarer profiles
 */
export async function getSeafarerProfiles(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: SeafarerProfile[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SEAFARER.PROFILES}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: SeafarerProfile[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /seafarers/profiles/{id}/
 *
 * Get a specific seafarer profile.
 *
 * @param id - Profile ID
 * @returns Seafarer profile detail
 */
export async function getSeafarerProfile(id: string): Promise<SeafarerProfile> {
  try {
    const response = await apiClient.get<SeafarerProfile>(
      API_ENDPOINTS.SEAFARER.PROFILE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Sea Service ─────────────────────────────────────────────────────────────

/**
 * GET /seafarers/sea-service/
 *
 * List sea service records for the current user.
 * Employers/admins can view all records.
 *
 * @param params - Optional search and pagination
 * @returns Paginated list of sea service records
 */
export async function getSeaServiceRecords(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: SeaServiceRecord[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SEAFARER.SEA_SERVICE}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: SeaServiceRecord[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /seafarers/sea-service/
 *
 * Create a new sea service record.
 *
 * @param data - Sea service record data
 * @returns Created sea service record
 */
export async function createSeaServiceRecord(
  data: SeaServiceRecordInput
): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.post<SeaServiceRecord>(
      API_ENDPOINTS.SEAFARER.SEA_SERVICE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /seafarers/sea-service/{id}/
 *
 * Get a single sea service record.
 *
 * @param id - Sea service record ID
 * @returns Sea service record detail
 */
export async function getSeaServiceRecord(id: string): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.get<SeaServiceRecord>(
      API_ENDPOINTS.SEAFARER.SEA_SERVICE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PUT /seafarers/sea-service/{id}/
 *
 * Update a sea service record.
 *
 * @param id - Sea service record ID
 * @param data - Updated sea service data
 * @returns Updated sea service record
 */
export async function updateSeaServiceRecord(
  id: string,
  data: Partial<SeaServiceRecordInput>
): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.put<SeaServiceRecord>(
      API_ENDPOINTS.SEAFARER.SEA_SERVICE_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /seafarers/sea-service/{id}/
 *
 * Delete a sea service record.
 *
 * @param id - Sea service record ID
 */
export async function deleteSeaServiceRecord(id: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.SEAFARER.SEA_SERVICE_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Certifications ──────────────────────────────────────────────────────────

/**
 * GET /seafarers/certifications/
 *
 * List certifications for the current user.
 *
 * @param params - Optional search and pagination
 * @returns Paginated list of certifications
 */
export async function getSeafarerCertifications(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: SeafarerCertification[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SEAFARER.CERTIFICATIONS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: SeafarerCertification[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /seafarers/certifications/
 *
 * Create a new certification.
 *
 * @param data - Certification data
 * @returns Created certification
 */
export async function createSeafarerCertification(
  data: SeafarerCertificationInput
): Promise<SeafarerCertification> {
  try {
    const response = await apiClient.post<SeafarerCertification>(
      API_ENDPOINTS.SEAFARER.CERTIFICATIONS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /seafarers/certifications/{id}/
 *
 * Get a single certification.
 *
 * @param id - Certification ID
 * @returns Certification detail
 */
export async function getSeafarerCertification(
  id: string
): Promise<SeafarerCertification> {
  try {
    const response = await apiClient.get<SeafarerCertification>(
      API_ENDPOINTS.SEAFARER.CERTIFICATION_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PUT /seafarers/certifications/{id}/
 *
 * Update a certification.
 *
 * @param id - Certification ID
 * @param data - Updated certification data
 * @returns Updated certification
 */
export async function updateSeafarerCertification(
  id: string,
  data: Partial<SeafarerCertificationInput>
): Promise<SeafarerCertification> {
  try {
    const response = await apiClient.put<SeafarerCertification>(
      API_ENDPOINTS.SEAFARER.CERTIFICATION_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /seafarers/certifications/{id}/
 *
 * Delete a certification.
 *
 * @param id - Certification ID
 */
export async function deleteSeafarerCertification(id: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.SEAFARER.CERTIFICATION_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Documents ───────────────────────────────────────────────────────────────

/**
 * GET /seafarers/documents/
 *
 * List documents for the current user.
 *
 * @param params - Optional search and pagination
 * @returns Paginated list of documents
 */
export async function getSeafarerDocuments(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: SeafarerDocument[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SEAFARER.DOCUMENTS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: SeafarerDocument[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /seafarers/documents/
 *
 * Create a new seafarer document.
 *
 * @param data - Document data
 * @returns Created document
 */
export async function createSeafarerDocument(
  data: SeafarerDocumentInput
): Promise<SeafarerDocument> {
  try {
    const formData = new FormData();
    formData.append('document_type', data.documentType);
    if (data.documentNumber) formData.append('document_number', data.documentNumber);
    if (data.issuingCountry) formData.append('issuing_country', data.issuingCountry);
    if (data.issuingAuthority) formData.append('issuing_authority', data.issuingAuthority);
    if (data.issueDate) formData.append('issue_date', data.issueDate);
    if (data.expiryDate) formData.append('expiry_date', data.expiryDate);
    if (data.isPrimary !== undefined) formData.append('is_primary', String(data.isPrimary));
    if (data.documentFile) formData.append('document_file', data.documentFile);

    const response = await apiClient.post<SeafarerDocument>(
      API_ENDPOINTS.SEAFARER.DOCUMENTS,
      formData
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}


export async function createSeafarerVisa(data: SeafarerVisaInput): Promise<SeafarerDocument> {
  try {
    const response = await apiClient.post<SeafarerDocument>(API_ENDPOINTS.SEAFARER.VISAS, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateSeafarerVisa(
  id: string,
  data: SeafarerVisaInput
): Promise<SeafarerDocument> {
  try {
    const response = await apiClient.patch<SeafarerDocument>(
      API_ENDPOINTS.SEAFARER.VISA_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /seafarers/documents/{id}/
 *
 * Get a single document.
 *
 * @param id - Document ID
 * @returns Document detail
 */
export async function getSeafarerDocument(id: string): Promise<SeafarerDocument> {
  try {
    const response = await apiClient.get<SeafarerDocument>(
      API_ENDPOINTS.SEAFARER.DOCUMENT_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /seafarers/documents/{id}/
 *
 * Delete a seafarer document.
 *
 * @param id - Document ID
 */
export async function deleteSeafarerDocument(id: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.SEAFARER.DOCUMENT_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /seafarers/documents/{id}/download/
 *
 * Download a seafarer document file.
 * Returns a Blob for direct download handling.
 *
 * @param id - Document ID
 * @returns Document file as Blob
 */
export async function downloadSeafarerDocument(id: string): Promise<Blob> {
  try {
    const response = await apiClient.get<Blob>(
      API_ENDPOINTS.SEAFARER.DOCUMENT_DOWNLOAD(id),
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Search ──────────────────────────────────────────────────────────────────

/**
 * GET /seafarers/search/
 *
 * Search available seafarers — employer/admin only.
 * Returns stripped PII data for privacy.
 *
 * @param params - Optional search and pagination
 * @returns Paginated list of public seafarer profiles
 */
export async function searchSeafarers(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: Array<{
    id: string;
    user: { firstName: string; lastName: string };
    currentRank: { rankName: string } | null;
    yearsOfExperience: number | null;
    availabilityStatus: string;
    profileCompletionPercent: number;
  }>;
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SEAFARER.SEARCH}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: Array<{
        id: string;
        user: { firstName: string; lastName: string };
        currentRank: { rankName: string } | null;
        yearsOfExperience: number | null;
        availabilityStatus: string;
        profileCompletionPercent: number;
      }>;
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

/**
 * GET /seafarers/profile/dashboard/
 *
 * Return seafarer user dashboard data including stats and profile summary.
 *
 * @returns Seafarer dashboard data
 */
export async function getSeafarerDashboard(): Promise<SeafarerDashboardData> {
  try {
    const response = await apiClient.get<SeafarerDashboardData>(
      API_ENDPOINTS.SEAFARER.DASHBOARD
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Write-enabled profile / sea service / certification helpers ─────────────

export interface SeafarerProfileWriteInput {
  currentRankId?: number | null;
  targetRankId?: number | null;
  departmentId?: number | null;
  primaryVesselCategoryId?: number | null;
  yearsOfExperience?: number | null;
  availabilityStatusId?: number | null;
  availabilityStatus?: string;
  availableFrom?: string | null;
  preferredContractDurationId?: number | null;
  preferredContractDuration?: number | null;
  expectedSalaryUsd?: number | null;
  preferredVesselTypeIds?: number[];
  preferredDepartmentIds?: number[];
  preferredTradingAreaIds?: number[];
  preferredTradingAreas?: string[];
  preferredContractTypeIds?: number[];
  willingToRelocate?: boolean;
  hasTankerExperience?: boolean;
  monthsInRank?: number | null;
  monthsWithOperator?: number | null;
  monthsOnVesselCategory?: number | null;
  isCargoWatchkeeper?: boolean;
  /** Skill taxonomy IDs (integer PKs). */
  skills?: number[];
  /** Language taxonomy IDs (integer PKs from seamaster.Language). */
  languages?: number[];
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
}

export interface SeaServiceWriteInput {
  vesselName: string;
  vesselTypeId?: number | null;
  imoNumber?: string;
  rankId?: number | null;
  rankOther?: string;
  employerName?: string;
  agencyName?: string;
  dateFrom: string;
  dateTo?: string | null;
  isCurrent?: boolean;
  grosstonnage?: number | null;
  enginePowerKw?: number | null;
  tradingArea?: string;
  duties?: string;
}

export interface CertificationWriteInput {
  certName: string;
  certificationTypeId?: number | null;
  certificateNumber?: string;
  issuingAuthority?: string;
  issueDate: string;
  expiryDate?: string | null;
}

/** PATCH /seafarers/profiles/my-profile/ */
export async function patchMySeafarerProfile(
  data: Partial<SeafarerProfileWriteInput>
): Promise<SeafarerProfile> {
  try {
    const response = await apiClient.patch<SeafarerProfile>(
      API_ENDPOINTS.SEAFARER.MY_PROFILE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/** GET /seafarers/sea-service/ */
export async function getMySeaService(): Promise<SeaServiceRecord[]> {
  try {
    const response = await apiClient.get<{
      count: number;
      results: SeaServiceRecord[];
    }>(API_ENDPOINTS.SEAFARER.SEA_SERVICE);
    return response.data.results ?? (response.data as unknown as SeaServiceRecord[]);
  } catch (error) {
    throw handleApiError(error);
  }
}

/** POST /seafarers/sea-service/ */
export async function createSeaService(
  data: SeaServiceWriteInput
): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.post<SeaServiceRecord>(
      API_ENDPOINTS.SEAFARER.SEA_SERVICE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/** PATCH /seafarers/sea-service/{id}/ */
export async function updateSeaService(
  id: number,
  data: Partial<SeaServiceWriteInput>
): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.patch<SeaServiceRecord>(
      `${API_ENDPOINTS.SEAFARER.SEA_SERVICE}/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/** DELETE /seafarers/sea-service/{id}/ */
export async function deleteSeaService(id: number): Promise<void> {
  try {
    await apiClient.delete(`${API_ENDPOINTS.SEAFARER.SEA_SERVICE}/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
}

/** GET /seafarers/certifications/ */
export async function getMyCertifications(): Promise<SeafarerCertification[]> {
  try {
    const response = await apiClient.get<{
      count: number;
      results: SeafarerCertification[];
    }>(API_ENDPOINTS.SEAFARER.CERTIFICATIONS);
    return (
      response.data.results ?? (response.data as unknown as SeafarerCertification[])
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

/** POST /seafarers/certifications/ */
export async function createCertification(
  data: CertificationWriteInput
): Promise<SeafarerCertification> {
  try {
    const response = await apiClient.post<SeafarerCertification>(
      API_ENDPOINTS.SEAFARER.CERTIFICATIONS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/** DELETE /seafarers/certifications/{id}/ */
export async function deleteCertification(id: number): Promise<void> {
  try {
    await apiClient.delete(`${API_ENDPOINTS.SEAFARER.CERTIFICATIONS}/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
}
